import { useAtomValue } from "@effect/atom-react";
import { useMemo } from "react";

import type { EnvironmentId, ServerSettings } from "@cleo/contracts";

import { primaryServerSettingsAtom } from "../state/server";
import { useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import type { WorldSessionLaunchProject } from "./worldSessionFirstExecution";

export type WorldWorkspaceBindings = ServerSettings["worldWorkspaceBindings"];

/**
 * DATED 2026-09-07: the code-root label the Context Rail's loading skeleton
 * may claim from a device-known binding. A DEFINED folder list is the
 * binding's exclusive authority (WorldWorkspaceBinding) and git-ness is
 * detected per folder on the server, so in folder-list mode the skeleton
 * claims no code root and the catalog's answer seats it — never a legacy
 * pointer's label that the answer then withdraws (witnessed 2026-09-07: the
 * Codebase door stood for the catalog's whole read on a World whose list
 * holds no repository, then vanished). A legacy binding with no list keeps
 * its pointer exactly as before.
 */
export function skeletonCodeRootLabel(
  binding: WorldWorkspaceBindings[string] | undefined,
): string | null {
  if (binding === undefined) return null;
  if (binding.folders !== undefined) return null;
  return binding.defaultExecutionProjectId ?? null;
}

/**
 * The offered default HOME for a World: `~/Neucleos/<World>` (WB4 — the
 * OS-level folder that matches the World's name, so shortcuts to it read as
 * the World; the person drops file or code folders inside it). The name
 * leans on the display name (falling back to the key), flattened of
 * path-hostile characters; the server's folder ceremony expands the `~` and
 * creates the folder, so this stays a pure suggestion.
 */
export function defaultWorldHomeFolder(worldName: string | null, worldKey: string): string {
  const cleaned = (worldName ?? "")
    .trim()
    .replace(/[/\\:]+/g, "-")
    .replace(/^\.+/, "");
  return `~/Neucleos/${cleaned.length > 0 ? cleaned : worldKey}`;
}

/**
 * Launch grounding (principal rulings 2026-08-28, ending in the folder-list
 * model). Child birth lawfully inherits the parent execution. Root birth
 * grounds on the user's file-system DEFAULTS from Settings → Connections:
 * the World's ordered folder list when one is written (`folders[0]` is the
 * session home; git-ness is detected, never declared), else the legacy
 * pointer/filesRoot ladder for Worlds that never wrote a list. Sessions
 * never require a code folder, and the context rail is the runtime
 * authority for what a session actually sees. There is no fallback to the
 * first project, home directory, drive search, or title match.
 */
export type WorldSessionLaunchGrounding =
  | { readonly kind: "parent"; readonly project: WorldSessionLaunchProject }
  | {
      readonly kind: "parent-project-missing";
      readonly environmentId: EnvironmentId;
      readonly projectId: string;
    }
  | { readonly kind: "bound"; readonly project: WorldSessionLaunchProject }
  | {
      readonly kind: "files-root";
      readonly environmentId: EnvironmentId;
      readonly filesRoot: string;
    }
  | { readonly kind: "unbound" }
  | {
      readonly kind: "binding-broken";
      readonly environmentId: EnvironmentId;
      readonly projectId: string;
    };

export function resolveWorldSessionLaunchGrounding(input: {
  readonly worldKey: string;
  readonly projects: ReadonlyArray<WorldSessionLaunchProject>;
  readonly bindingsByEnvironment: ReadonlyMap<EnvironmentId, WorldWorkspaceBindings>;
  readonly parentExecution: {
    readonly environmentId: EnvironmentId;
    readonly projectId: string;
  } | null;
}): WorldSessionLaunchGrounding {
  const byKey = (environmentId: string, projectId: string) =>
    input.projects.find(
      (candidate) => candidate.environmentId === environmentId && candidate.projectId === projectId,
    ) ?? null;

  if (input.parentExecution !== null) {
    const parentProject = byKey(
      input.parentExecution.environmentId,
      input.parentExecution.projectId,
    );
    if (parentProject !== null) return { kind: "parent", project: parentProject };
    return {
      kind: "parent-project-missing",
      environmentId: input.parentExecution.environmentId,
      projectId: input.parentExecution.projectId,
    };
  }

  // The folder-list model (principal ruling 2026-08-28): a DEFINED folders
  // list is the exclusive authority — World-parented births ground in
  // folders[0], and an explicitly emptied list is unbound, never a legacy
  // fallback. Legacy pointer/filesRoot bindings serve only Worlds that have
  // never written a list.
  // WB5: the person's explicit choices are the only defaults when written.
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    const chosen = bindings[input.worldKey]?.sourceFolders;
    if (chosen === undefined) continue;
    const root = chosen.codebase[0] ?? chosen.files[0] ?? null;
    if (root === null) return { kind: "unbound" };
    const project = input.projects.find(
      (candidate) => candidate.environmentId === environmentId && candidate.workspaceRoot === root,
    );
    return project
      ? { kind: "bound", project }
      : { kind: "files-root", environmentId, filesRoot: root };
  }
  let legacyEligible = true;
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    const folders = bindings[input.worldKey]?.folders;
    if (folders === undefined) continue;
    legacyEligible = false;
    const first = folders.find((folder) => typeof folder === "string" && folder.trim().length > 0);
    if (first !== undefined) return { kind: "files-root", environmentId, filesRoot: first };
  }
  if (!legacyEligible) return { kind: "unbound" };

  let broken: WorldSessionLaunchGrounding | null = null;
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    const binding = bindings[input.worldKey];
    if (binding?.defaultExecutionProjectId === undefined) continue;
    const bound = byKey(environmentId, binding.defaultExecutionProjectId);
    if (bound !== null) return { kind: "bound", project: bound };
    broken ??= {
      kind: "binding-broken",
      environmentId,
      projectId: binding.defaultExecutionProjectId,
    };
  }

  // Legacy Files-folder lane; a DANGLING legacy pointer no longer gates
  // launch (principal correction 2026-08-28, "still requiring project?").
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    const filesRoot = bindings[input.worldKey]?.filesRoot;
    if (typeof filesRoot === "string" && filesRoot.trim().length > 0) {
      return { kind: "files-root", environmentId, filesRoot };
    }
  }

  // Only with no folder anywhere does the dangling binding block, as its
  // own typed repair — nothing is substituted from other projects.
  if (broken !== null) return broken;

  return { kind: "unbound" };
}

/**
 * The World's device-local files root in one environment (O11). Under the
 * folder-list model this is the list's first folder; a World that never
 * wrote a list keeps its legacy `filesRoot`. Exact World identity only; no
 * fallback to another World's or another environment's root.
 */
export function resolveWorldFilesRoot(input: {
  readonly worldKey: string;
  readonly environmentId: EnvironmentId;
  readonly bindingsByEnvironment: ReadonlyMap<EnvironmentId, WorldWorkspaceBindings>;
}): string | null {
  const binding = input.bindingsByEnvironment.get(input.environmentId)?.[input.worldKey];
  if (binding?.sourceFolders !== undefined) return binding.sourceFolders.files[0] ?? null;
  if (binding?.folders !== undefined) {
    const first = binding.folders.find(
      (folder) => typeof folder === "string" && folder.trim().length > 0,
    );
    return first ?? null;
  }
  const root = binding?.filesRoot;
  return typeof root === "string" && root.trim().length > 0 ? root : null;
}

/**
 * Each connected environment's own World → workspace bindings. Bindings are
 * environment-local server settings, so every environment answers only for
 * itself; the primary environment's settings fill in when its config stream
 * has not yet attached to the environments list.
 */
export function useWorldWorkspaceBindings(): ReadonlyMap<EnvironmentId, WorldWorkspaceBindings> {
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primarySettings = useAtomValue(primaryServerSettingsAtom);
  return useMemo(() => {
    const bindings = new Map<EnvironmentId, WorldWorkspaceBindings>();
    for (const environment of environments) {
      const settings = environment.serverConfig?.settings;
      if (settings !== undefined) {
        bindings.set(environment.environmentId, settings.worldWorkspaceBindings);
      }
    }
    if (primaryEnvironmentId !== null && !bindings.has(primaryEnvironmentId)) {
      bindings.set(primaryEnvironmentId, primarySettings.worldWorkspaceBindings);
    }
    return bindings;
  }, [environments, primaryEnvironmentId, primarySettings]);
}
